class AjaxSearch {
  constructor(container) {
    this.container = container;
    this.searchInput = container.querySelector('[data-search-input]');
    this.searchResults = container.querySelector('[data-search-results]');
    this.searchForm = container.querySelector('[data-search-form]');
    this.loadingState = this.searchResults?.querySelector('.ajax-search-loading-state');
    this.debounceTimer = null;
    this.currentRequest = null;
    this.scrollTimer = null;
    this.lastScrollY = undefined;
    this.scrollHidingDisabled = false;
    
    // Determine if this is mobile or desktop
    this.isMobile = container.closest('.mobile_search') !== null;
    
    this.init();
  }

  init() {
    if (!this.searchInput) return;
    
    this.searchInput.addEventListener('input', this.handleInput.bind(this));
    this.searchInput.addEventListener('focus', this.handleFocus.bind(this));
    
    // Add scroll listener only once per instance
    window.addEventListener('scroll', this.handleScroll.bind(this), { passive: true });
    
    // Handle form submission
    if (this.searchForm) {
      this.searchForm.addEventListener('submit', this.handleSubmit.bind(this));
    }
    
    // Update ARIA attributes
    this.searchInput.setAttribute('aria-expanded', 'false');
  }

  handleInput(event) {
    const query = event.target.value.trim();
    
    // Clear previous timer
    clearTimeout(this.debounceTimer);
    
    if (query.length < 2) {
      this.hideResults();
      return;
    }
    
    // Debounce the search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  handleFocus(event) {
    const query = event.target.value.trim();
    if (query.length >= 2) {
      this.performSearch(query);
    }
  }

  handleSubmit(event) {
    const query = this.searchInput.value.trim();
    if (query) {
      // Let the form submit naturally to the search page
      return;
    }
    event.preventDefault();
  }

  handleScroll() {
    if (this.searchResults && 
        this.searchResults.style.display !== 'none' && 
        !this.scrollHidingDisabled) {
      
      const currentScrollY = window.scrollY;
      
      // Initialize last scroll position
      if (this.lastScrollY === undefined) {
        this.lastScrollY = currentScrollY;
        return;
      }
      
      // Only hide when scrolling down
      if (currentScrollY > this.lastScrollY) {
        clearTimeout(this.scrollTimer);
        this.scrollTimer = setTimeout(() => {
          this.hideResults();
          this.lastScrollY = undefined; // Reset for next time
        }, 100);
      }
      
      this.lastScrollY = currentScrollY;
    }
  }

  disableScrollHiding() {
    this.scrollHidingDisabled = true;
  }

  enableScrollHiding() {
    this.scrollHidingDisabled = false;
  }

  async performSearch(query) {
    // Cancel previous request
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
    
    // Show loading state
    this.showLoading();
    
    try {
      // Create new AbortController for this request
      const controller = new AbortController();
      this.currentRequest = controller;
      
// Check if we're on the Canadian site
const isCanadianSite = window.location.pathname.includes('/en-ca/') || window.location.pathname.startsWith('/en-ca');

// Build the search URL based on the site locale
const searchUrl = isCanadianSite 
  ? `/en-ca/search?q=${encodeURIComponent(query)}&type=product&view=ajax`
  : `/search?q=${encodeURIComponent(query)}&type=product&view=ajax`;

      
      const response = await fetch(searchUrl, {
        signal: controller.signal,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const html = await response.text();
      this.displayResults(html);
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
        this.showError();
      }
    } finally {
      this.currentRequest = null;
    }
  }

  showLoading() {
    if (this.searchResults && this.loadingState) {
      this.searchResults.innerHTML = '';
      this.loadingState.style.display = 'block';
      this.searchResults.appendChild(this.loadingState);
      this.searchResults.style.display = 'block';
      this.searchInput.setAttribute('aria-expanded', 'true');
    }
  }

  displayResults(html) {
    if (this.searchResults) {
      this.searchResults.innerHTML = html;
      this.searchResults.style.display = 'block';
      this.searchInput.setAttribute('aria-expanded', 'true');
      
      // Only show backdrop on desktop
      if (!this.isMobile) {
        this.showBackdrop();
      }
      
      this.addResultsInteractivity();
    }
  }

  showError() {
    if (this.searchResults) {
      this.searchResults.innerHTML = `
        <div class="ajax-search-error" style="padding: 20px; text-align: center;">
          <p>Sorry, something went wrong. Please try again.</p>
        </div>
      `;
    }
  }

  hideResults() {
    if (this.searchResults) {
      this.searchResults.style.display = 'none';
      this.searchInput.setAttribute('aria-expanded', 'false');
      
      // Only hide backdrop on desktop
      if (!this.isMobile) {
        this.hideBackdrop();
      }
      
      this.enableScrollHiding(); // Re-enable scroll hiding when results are hidden
      this.lastScrollY = undefined; // Reset scroll position tracking
    }
  }

  showBackdrop() {
    // Only create backdrop for desktop
    if (this.isMobile) return;
    
    let backdrop = document.getElementById('ajax-predictivebg');
    if (!backdrop) {
      backdrop = document.createElement('div');
      backdrop.id = 'ajax-predictivebg';
      backdrop.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(0, 0, 0, 0.7);
        z-index: 1000;
        display: block;
      `;
      document.body.appendChild(backdrop);
      
      // Add click handler to close on backdrop click
      backdrop.addEventListener('click', () => {
        // Close all search instances
        window.ajaxSearchInstances?.forEach(instance => {
          instance.hideResults();
        });
      });
    } else {
      backdrop.style.display = 'block';
    }
  }

  hideBackdrop() {
    if (this.isMobile) return;
    
    const backdrop = document.getElementById('ajax-predictivebg');
    if (backdrop) {
      backdrop.style.display = 'none';
    }
  }

  addResultsInteractivity() {
    // Handle close button
    const closeButton = this.searchResults.querySelector('.ajax-search-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => {
        this.hideResults();
        this.searchInput.value = '';
      });
    }

    // Handle "search for" button
    const searchButton = this.searchResults.querySelector('.ajax-search-button button');
    if (searchButton) {
      searchButton.addEventListener('click', () => {
        this.searchForm.submit();
      });
    }

    // Add click handlers for results
    const resultItems = this.searchResults.querySelectorAll('.ajax-search-list-item');
    resultItems.forEach(item => {
      // Disable scroll hiding when hovering over results
      item.addEventListener('mouseenter', () => {
        this.disableScrollHiding();
      });
      
      item.addEventListener('mouseleave', () => {
        this.enableScrollHiding();
      });
      
      item.addEventListener('click', (e) => {
        const link = item.querySelector('a');
        if (link && !e.target.closest('a')) {
          window.location.href = link.href;
        }
      });
    });

    // Disable scroll hiding when mouse is over the entire results area
    this.searchResults.addEventListener('mouseenter', () => {
      this.disableScrollHiding();
    });
    
    this.searchResults.addEventListener('mouseleave', () => {
      this.enableScrollHiding();
    });

    // Add keyboard navigation
    this.addKeyboardNavigation();
  }

  addKeyboardNavigation() {
    const items = this.searchResults?.querySelectorAll('.ajax-search-list-item');
    if (!items) return;
    
    let currentIndex = -1;
    
    this.searchInput.addEventListener('keydown', (e) => {
      if (!this.searchResults || this.searchResults.style.display === 'none') return;
      
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          currentIndex = Math.min(currentIndex + 1, items.length - 1);
          this.highlightItem(items, currentIndex);
          break;
        case 'ArrowUp':
          e.preventDefault();
          currentIndex = Math.max(currentIndex - 1, -1);
          this.highlightItem(items, currentIndex);
          break;
        case 'Enter':
          if (currentIndex >= 0 && items[currentIndex]) {
            e.preventDefault();
            const link = items[currentIndex].querySelector('a');
            if (link) {
              window.location.href = link.href;
            }
          }
          break;
        case 'Escape':
          this.hideResults();
          currentIndex = -1;
          break;
      }
    });
  }

  highlightItem(items, index) {
    items.forEach((item, i) => {
      item.classList.toggle('highlighted', i === index);
      item.setAttribute('aria-selected', i === index ? 'true' : 'false');
    });
  }

  // Clean up event listeners when needed (optional)
  destroy() {
    window.removeEventListener('scroll', this.handleScroll.bind(this));
    clearTimeout(this.scrollTimer);
    clearTimeout(this.debounceTimer);
    if (this.currentRequest) {
      this.currentRequest.abort();
    }
  }
}

// Global click outside handler
function handleGlobalClickOutside(event) {
  if (event.target.id === 'ajax-predictivebg' || 
      (!event.target.closest('[data-search-container]') && 
       !event.target.closest('.ajax-search-wrapper'))) {
    
    // Close all search instances
    window.ajaxSearchInstances?.forEach(instance => {
      instance.hideResults();
    });
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  // Find all search containers and create instances
  const searchContainers = document.querySelectorAll('[data-search-container]');
  window.ajaxSearchInstances = [];
  
  searchContainers.forEach(container => {
    const instance = new AjaxSearch(container);
    window.ajaxSearchInstances.push(instance);
  });
  
  // Add global click outside handler
  document.addEventListener('click', handleGlobalClickOutside);
});
